import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage key - profile is a non-sensitive display cache (auth is an httpOnly cookie)
const CREATOR_PROFILE_KEY = "creator_profile";

export default function AuthVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");
  
  useEffect(() => {
    const token = searchParams.get("token");
    
    console.log("[AuthVerify] Starting verification...");
    console.log("[AuthVerify] Token present:", !!token);
    console.log("[AuthVerify] API base URL:", API);
    
    if (!token) {
      console.error("[AuthVerify] No token in URL");
      setStatus("error");
      setErrorMessage("Invalid sign-in link. No token provided.");
      return;
    }
    
    verifyToken(token);
  }, [searchParams]);
  
  const verifyToken = async (token) => {
    try {
      console.log("[AuthVerify] Calling verify endpoint...");
      
      const response = await axios.get(`${API}/auth/verify-magic-link`, {
        params: { token }
      });
      
      console.log("[AuthVerify] Response status:", response.status);
      console.log("[AuthVerify] Response success:", response.data?.success);
      
      if (response.data.success) {
        // Auth token is set as an httpOnly cookie by the backend.
        // Cache only non-sensitive profile for quick display.
        if (response.data.creator) {
          localStorage.setItem(CREATOR_PROFILE_KEY, JSON.stringify(response.data.creator));
        }

        console.log("[AuthVerify] Session established via httpOnly cookie");
        console.log("[AuthVerify] Creator:", response.data.creator?.email);
        
        setStatus("success");
        toast.success("Signed in successfully!");
        
        // Redirect to My Challenges after a brief delay to show success state
        setTimeout(() => {
          console.log("[AuthVerify] Redirecting to /my-challenges");
          navigate("/my-challenges", { replace: true });
        }, 1500);
      } else {
        throw new Error("Verification failed");
      }
    } catch (error) {
      console.error("[AuthVerify] Verification error:", error);
      console.error("[AuthVerify] Error response:", error.response?.data);
      console.error("[AuthVerify] Error status:", error.response?.status);
      
      setStatus("error");
      
      // Parse error message from backend
      const backendMessage = error.response?.data?.detail;
      if (backendMessage) {
        if (backendMessage.includes("expired")) {
          setErrorMessage("This sign-in link has expired. Please request a new one.");
        } else if (backendMessage.includes("used")) {
          setErrorMessage("This sign-in link has already been used. Please request a new one.");
        } else if (backendMessage.includes("Invalid")) {
          setErrorMessage("This sign-in link is invalid. Please request a new one.");
        } else {
          setErrorMessage(backendMessage);
        }
      } else {
        setErrorMessage("Unable to verify sign-in link. Please try again or request a new link.");
      }
      
      toast.error("Sign-in failed");
    }
  };
  
  const handleRequestNewLink = () => {
    // Navigate to home with login modal open
    navigate("/?login=1", { replace: true });
  };
  
  return (
    <div className="min-h-screen bg-paper flex items-center justify-center py-8 px-4">
      <Card className="max-w-md w-full rounded-2xl shadow-lg">
        <CardContent className="p-8 text-center">
          {status === "verifying" && (
            <>
              <Loader2 className="w-16 h-16 text-ember mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-2xl text-sage-900 mb-2">
                Signing you in...
              </h1>
              <p className="font-body text-muted-foreground">
                Please wait while we verify your sign-in link.
              </p>
            </>
          )}
          
          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl text-sage-900 mb-2">
                Welcome back!
              </h1>
              <p className="font-body text-muted-foreground">
                Redirecting you to your challenges...
              </p>
            </>
          )}
          
          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl text-sage-900 mb-2">
                Sign-in Failed
              </h1>
              <p className="font-body text-muted-foreground mb-6">
                {errorMessage}
              </p>
              <Button
                onClick={handleRequestNewLink}
                className="rounded-full bg-ember hover:bg-ember/90"
                data-testid="request-new-link-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Request New Sign-in Link
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
